import React, { useState, useEffect } from "react";
import {
  FaLinkedin,
  FaExclamationTriangle,
  FaArrowLeft,
  FaArrowRight,
  FaRegCalendarAlt,
  FaRegCheckCircle,
  FaRegTimesCircle,
} from "react-icons/fa";
import ReactMarkdown from "react-markdown";

const EmailPreview = ({ campaign, selectedPersona, selectedLead }) => {
  const [emailBodies, setEmailBodies] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [currentTemplate, setCurrentTemplate] = useState(0);

  // The sequence of email templates
  const emailSequence = [
    {
      day: 0,
      templateId: "day0",
      name: "Spark",
      goal: "Introduce and highlight one key result",
      ctaType: "Quick chat suggestion",
      subject: "Cut Landfill Fees 30%? Quick intro from ReWorld",
    },
    {
      day: 3,
      templateId: "day3",
      name: "Value Add",
      goal: "Share mini case study and video link",
      ctaType: "Offer to share calculator/tool",
      subject: "60-sec video: How [peer] hit zero-waste targets",
    },
    {
      day: 6,
      templateId: "day6",
      name: "Pain/Insight",
      goal: "Highlight hidden costs, ask engaging question",
      ctaType: "Offer to share breakdown/analysis",
      subject: "Hidden cost on your waste invoice?",
    },
    {
      day: 10,
      templateId: "day10",
      name: "Social Proof",
      goal: "Share quantified peer outcome, offer calendar link",
      ctaType: "Meeting calendar link",
      subject: "[Peer] saved $X in hauling—see the numbers?",
    },
    {
      day: 14,
      templateId: "day14",
      name: "Break-up",
      goal: "Friendly final outreach with easy out",
      ctaType: "Closing the loop",
      subject: "Should I close the file?",
    },
  ];

  // Generate email content on initial load and when key props change
  useEffect(() => {
    generateAllEmailContent();
  }, [campaign, selectedPersona, selectedLead]);

  // Function to generate all email templates
  const generateAllEmailContent = () => {
    setIsGenerating(true);

    // In a real implementation, we would call the API for each template
    // For this demo, we'll create sample content for each
    const leadName = selectedLead?.name?.split(" ")[0] || "there";
    const companyName = selectedLead?.company || "your company";
    const templates = {};

    // Simulate API delay
    setTimeout(() => {
      // Generate content for each template in the sequence
      emailSequence.forEach((template) => {
        let emailContent = "";

        switch (template.templateId) {
          case "day0": // Spark
            emailContent = `Hi ${leadName},

I noticed that ${companyName} has been investing in improving waste management efficiency, and I thought you might be interested in how ReWorld could help reduce your landfill fees by up to 30%.

Our ReDirect360 solution has helped companies like yours turn compliance costs into ESG wins. Would you be open to a quick chat about your current waste handling processes?

Thanks,
${
  selectedPersona?.signature ||
  "Alex Rivera\nSenior Sustainable Waste Solutions Consultant\nReWorld"
}`;
            break;

          case "day3": // Value Add
            emailContent = `Hi ${leadName},

I wanted to share a quick 60-second video showing how one of our clients in your industry achieved their zero-waste targets using our solutions:

[Video Link]

They were struggling with similar challenges to what many ${companyName} peers face. Would you be interested in our waste-cost calculator that shows potential savings?

Best,
${
  selectedPersona?.signature ||
  "Alex Rivera\nSenior Sustainable Waste Solutions Consultant\nReWorld"
}`;
            break;

          case "day6": // Pain/Insight
            emailContent = `Hi ${leadName},

Did you know that most waste invoices contain hidden costs that many companies overlook? Our analysis shows that businesses like ${companyName} typically overpay by 15-20% due to these hidden charges.

I'd be happy to share a quick breakdown of the most common hidden costs to look for. Would that be helpful?

Regards,
${
  selectedPersona?.signature ||
  "Alex Rivera\nSenior Sustainable Waste Solutions Consultant\nReWorld"
}`;
            break;

          case "day10": // Social Proof
            emailContent = `Hi ${leadName},

I thought you might be interested to know that [Peer Company] recently saved $43,000 annually in hauling costs after implementing our solutions.

They operate in a similar space to ${companyName} and faced many of the same challenges. Would you like to see the numbers behind their success?

You can book a 15-minute slot here: ${
              campaign.calendlyLink || "https://calendly.com/reworld-intro/demo"
            }

Thanks,
${
  selectedPersona?.signature ||
  "Alex Rivera\nSenior Sustainable Waste Solutions Consultant\nReWorld"
}`;
            break;

          case "day14": // Break-up
            emailContent = `Hi ${leadName},

I've reached out a few times about how ReWorld might be able to help ${companyName} reduce waste management costs and improve sustainability metrics.

I'm going to assume the timing isn't right or this isn't a priority right now. If that changes, feel free to reach out - or let me know if you'd prefer I close your file.

All the best,
${
  selectedPersona?.signature ||
  "Alex Rivera\nSenior Sustainable Waste Solutions Consultant\nReWorld"
}`;
            break;

          default:
            emailContent = `Hi ${leadName},\n\nDefault template content.\n\nRegards,\n${
              selectedPersona?.signature || "Alex"
            }`;
        }

        templates[template.templateId] = emailContent;
      });

      setEmailBodies(templates);
      setIsGenerating(false);
    }, 1500);
  };

  // Navigate between templates
  const goToPreviousTemplate = () => {
    setCurrentTemplate((prev) =>
      prev === 0 ? emailSequence.length - 1 : prev - 1
    );
  };

  const goToNextTemplate = () => {
    setCurrentTemplate((prev) =>
      prev === emailSequence.length - 1 ? 0 : prev + 1
    );
  };

  // Generate a single email template
  const regenerateCurrentEmail = () => {
    setIsGenerating(true);

    // In real implementation, this would call the API to regenerate just this template
    // For demo, we'll use a timeout
    setTimeout(() => {
      const templateId = emailSequence[currentTemplate].templateId;
      const leadName = selectedLead?.name?.split(" ")[0] || "there";
      const companyName = selectedLead?.company || "your company";

      // Create a regenerated version
      const regeneratedContent = `Hi ${leadName},

This is a regenerated email for the ${
        emailSequence[currentTemplate].name
      } template (Day ${emailSequence[currentTemplate].day}).

I've regenerated this email specifically for ${companyName} with a focus on ${
        emailSequence[currentTemplate].goal
      }.

Best regards,
${
  selectedPersona?.signature ||
  "Alex Rivera\nSenior Sustainable Waste Solutions Consultant\nReWorld"
}`;

      setEmailBodies((prev) => ({
        ...prev,
        [templateId]: regeneratedContent,
      }));

      setIsGenerating(false);
    }, 1200);
  };

  // Render different email frames based on preview mode
  const renderEmailFrame = () => {
    const currentEmailData = emailSequence[currentTemplate];
    const templateId = currentEmailData.templateId;
    const emailContent =
      emailBodies[templateId] ||
      "Email content will appear here once generated.";

    return (
      <div
        className={`
        border border-gray-200 rounded-lg overflow-hidden
        ${previewMode === "mobile" ? "max-w-xs mx-auto" : "w-full"}
      `}
      >
        {/* Email Header */}
        <div className="bg-gray-100 border-b border-gray-200 p-3">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-medium text-gray-700">
                To: {selectedLead?.name || "Lead Name"}
              </div>
              <div className="text-xs text-gray-500">
                {selectedLead?.email || "lead@company.com"}
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {new Date().toLocaleString()}
            </div>
          </div>
        </div>

        {/* Email Subject */}
        <div className="border-b border-gray-200 p-3">
          <div className="text-sm font-medium text-gray-800">
            Subject: {currentEmailData.subject}
          </div>
        </div>

        {/* Email Body */}
        <div className="p-4 bg-white">
          <div className="prose max-w-none text-gray-800 whitespace-pre-wrap">
            <ReactMarkdown>{emailContent}</ReactMarkdown>
          </div>

          {/* Signature */}
          {selectedPersona?.linkedinUrl && (
            <div className="mt-4 text-xs text-blue-600">
              <a
                href={selectedPersona.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center"
              >
                <FaLinkedin className="mr-1" />
                LinkedIn Profile
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Email Preview</h2>

      {campaign.status === "draft" && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                This campaign is still in draft mode. Complete all steps and
                launch the campaign to start sending emails.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Template Navigation */}
      <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">5-Touch Campaign Sequence</h3>
          <div className="text-sm text-gray-600">
            {currentTemplate + 1} of {emailSequence.length}
          </div>
        </div>

        <div className="relative">
          {/* Timeline Bar */}
          <div className="absolute top-3 left-0 right-0 h-1 bg-gray-200 z-0"></div>

          {/* Timeline Points */}
          <div className="flex justify-between relative z-10">
            {emailSequence.map((template, index) => (
              <button
                key={index}
                onClick={() => setCurrentTemplate(index)}
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                  currentTemplate === index
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-500 border-gray-300 hover:border-purple-300"
                }`}
                title={`Day ${template.day}: ${template.name}`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {/* Day Labels */}
          <div className="flex justify-between mt-2">
            {emailSequence.map((template, index) => (
              <div
                key={index}
                className="w-10 text-center text-xs text-gray-500"
              >
                Day {template.day}
              </div>
            ))}
          </div>
        </div>

        {/* Current Template Info */}
        <div className="mt-6 p-3 bg-white rounded border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-medium text-gray-900">
                {emailSequence[currentTemplate].name}
              </h4>
              <p className="text-sm text-gray-600">
                {emailSequence[currentTemplate].goal}
              </p>
            </div>
            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
              {emailSequence[currentTemplate].ctaType}
            </span>
          </div>
        </div>
      </div>

      {/* Preview Controls */}
      <div className="flex justify-between items-center mb-6">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setPreviewMode("desktop")}
            className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
              previewMode === "desktop"
                ? "bg-purple-600 text-white border-purple-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setPreviewMode("mobile")}
            className={`px-4 py-2 text-sm font-medium rounded-r-md border ${
              previewMode === "mobile"
                ? "bg-purple-600 text-white border-purple-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            Mobile
          </button>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={goToPreviousTemplate}
            className="p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            <FaArrowLeft />
          </button>

          <button
            onClick={regenerateCurrentEmail}
            disabled={isGenerating}
            className={`
              px-4 py-2 rounded-md text-sm font-medium
              ${
                isGenerating
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }
            `}
          >
            {isGenerating ? "Generating..." : "Regenerate Email"}
          </button>

          <button
            onClick={goToNextTemplate}
            className="p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            <FaArrowRight />
          </button>
        </div>
      </div>

      {/* Email Preview */}
      <div className="bg-gray-50 p-6 rounded-lg">
        {isGenerating ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          renderEmailFrame()
        )}
      </div>

      {/* Campaign Settings Summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center text-gray-700 mb-2">
            <FaRegCalendarAlt className="mr-2" />
            <h4 className="font-medium">Campaign Period</h4>
          </div>
          <p className="text-sm text-gray-600">
            {campaign.startDate
              ? campaign.startDate.toLocaleDateString()
              : "Start Date"}{" "}
            to{" "}
            {campaign.endDate
              ? campaign.endDate.toLocaleDateString()
              : "End Date"}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center text-gray-700 mb-2">
            <FaRegCheckCircle className="mr-2" />
            <h4 className="font-medium">Campaign Goal</h4>
          </div>
          <p className="text-sm text-gray-600">
            {campaign.goal || "Book a demo"}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center text-gray-700 mb-2">
            <FaRegTimesCircle className="mr-2" />
            <h4 className="font-medium">Stop Sequence</h4>
          </div>
          <p className="text-sm text-gray-600">
            {campaign.stopOnReply !== false
              ? "Stop if lead replies"
              : "Continue even if lead replies"}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-8">
        <button className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded font-medium">
          Edit Sequence
        </button>

        <div>
          <button className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded font-medium mr-2">
            Save Draft
          </button>
          <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-medium">
            Launch Campaign
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailPreview;
